import { Component, OnInit, Input, ChangeDetectorRef, ViewChild, ElementRef, OnChanges } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { UsersService } from 'src/app/users/users.service';
import { CommonDataService } from '../../../shared/services/common-data.service';
import { keyBy } from 'lodash-es';
import { SitesService } from '../../sites.service';
import { MultiTariffService } from 'src/app/admin/multi-tariff/multi-tariff.service';
import { Profile } from 'src/app/admin/multi-tariff/multi-tariff.model';

@Component({
	selector: 'app-site-edit',
	templateUrl: './site-edit.component.html',
	styleUrls: ['./site-edit.component.css'],
	viewProviders: [ { provide: ControlContainer, useExisting: NgForm } ]
})
export class SiteEditComponent implements OnInit , OnChanges {
	@ViewChild('siteNameComponent', { static: false }) siteNameComponent: ElementRef;
	@Input() site: any = {};
	@Input() enterpriseList: Array<any> = [];
	@Input() miniForm: boolean = false;
	@Input() restrictSpecialSite: boolean = false;
	@Input() customerEnterprises: Array<any> = [];
	@Input() showIotMeterOptions: boolean = false;
	@Input() hideEnterprise: boolean = false;
	@Input() hideSettings: boolean = false;
	@Input() hideAddressFields: boolean = false;
	// hides the SCT-only parts (FSEID, enterprise, settings, multi-tariff, recipient groups, contact cards) for non-SCT users
	@Input() hideInternalSections = false;

	timeZonesMenu = <any>[];
	minInstallationDate = new Date(1970, 1, 1);

	allMeterCustomers: any[] = [];
	allMeterSites: any[] = [];
	selectedMeterCustomerSites: any = [];

	multiTariffProfiles: Profile[] = [];
	canEditMultiTariffProfile = false;

	recipientGroups: any = [];
	siteRecipientGroupsUsersList = [];
	siteRecipientGroupsUsersIds = [];
	addressEditable = false;

	passwordModes = [
		{ value: 'site_level', label: 'site_edit.site_level_password' },
		{ value: 'device_level', label: 'site_edit.device_level_password' },
		{ value: 'no_password', label: 'site_edit.no_password' },
		{ value: 'no_touch', label: 'site_edit.no_touch' }
	];

	constructor(
		private commonData: CommonDataService,
		public usersService: UsersService,
		private siteService: SitesService,
		private multiTariffService: MultiTariffService,
		private translateService: TranslateService
	) {
	}

	ngOnInit() {
		this.timeZonesMenu = this.commonData.TimeZonesMenu;
		// set the default value of is_smart_rebates to true
		if (!Object.keys(this.site).includes('is_smart_rebates'))
			this.site.is_smart_rebates = true;

		if (!Object.keys(this.site).includes('address_manually_edited'))
			this.site.address_manually_edited = false;

		// set the default value of password_mode to no_touch if not set
		if (!this.site.password_mode)
			this.site.password_mode = 'no_touch';

		this.canEditMultiTariffProfile = this.usersService.hasAccessFunction('global_multi_tariff_profiles');
		if (this.canEditMultiTariffProfile)
			this.getMultiTariffProfiles();

		this.siteService.getAllSiteGroups(false).subscribe((res: any) => {
			this.recipientGroups = res;
			this.fillSiteRecipientGroups();
		});
	}

	ngOnChanges(changes: { site: any; }) {
		if (changes.site) {
			// Ensure password_mode is set when site data changes
			if (!this.site.password_mode) {
				this.site.password_mode = 'no_touch';
			}
			this.fillSiteRecipientGroups();
			if (this.showIotMeterOptions && 'is_contain_iotemeter_devices' in this.site && this.site.is_contain_iotemeter_devices)
				this.getIotEmterCustomerAndSites();
		}
	}

	fillSiteRecipientGroups() {
		const recipientGroups = [];

		if (this.site.id) {
			for (const group of this.recipientGroups) {
				if (group.sites_ids && group.sites_ids.includes(this.site.id))
					recipientGroups.push(group.id);
			}
		}

		if (recipientGroups.length) {
			this.site.recipient_groups = recipientGroups;
			this.updateRecipientGroupUsers();
		}
	}

	getMultiTariffProfiles() {
		this.multiTariffService.getAllProfiles().subscribe((profiles: Profile[]) => {
			this.multiTariffProfiles = [
				{ id: null, name: this.translateService.instant('multi_tariff.no_profile') },
				...(profiles || [])
			];
		});
	}

	enableAddressEdit() {
		this.addressEditable = true;
		this.site.address_manually_edited = true;
	}

	updateRecipientGroupUsers() {
		this.siteRecipientGroupsUsersList = [];
		this.siteRecipientGroupsUsersIds = [];

		const recipientGroups = keyBy(this.recipientGroups, 'id');

		for (const groupId of this.site.recipient_groups) {
			const groupData = recipientGroups[groupId];

			for (const user of groupData.usersInfo) {
				if (this.siteRecipientGroupsUsersIds.includes([user.id]))
					continue;

				this.siteRecipientGroupsUsersIds.push(user.id);
				this.siteRecipientGroupsUsersList.push({ id: user.id, name: `${user.first_name} ${user.last_name}`} );
			}
		}

		this.siteRecipientGroupsUsersList = structuredClone(this.siteRecipientGroupsUsersList);
		this.siteRecipientGroupsUsersIds = structuredClone(this.siteRecipientGroupsUsersIds);
	}

	ngAfterViewInit() {
		this.siteNameComponent.nativeElement.focus();
	}

	isContainIotEmeterDevices(enabled: boolean) {
		if (enabled && !this.allMeterCustomers.length)
			this.getIotEmterCustomerAndSites();
	}

	getIotEmterCustomerAndSites() {
		this.siteService.getIotemeterCustomersAndSites().subscribe((res: any) => {
			this.allMeterCustomers = res.iotMeterCustomersInfo;
			this.allMeterSites = res.iotMeterSitesInfo;
			this.selectedMeterCustomerSites = this.allMeterSites.filter(site => site.customer_id == this.site.iotemeter_customer_id || 0) || [];
		});
	}

	onCustomerSelect(customerId: number) {
		this.site.iotemeter_site_id = null;
		this.selectedMeterCustomerSites = this.allMeterSites.filter(site => site.customer_id == customerId) || [];
	}

	canEditPasswordMode(): boolean {
		return this.usersService.hasAccessPermission(null, 'noc', 'write');
	}

	showSiteSettingsInfo(): boolean {
		return this.usersService.hasAccessFunction('official_reporting') || this.usersService.hasAccessPermission(null, 'noc') || !this.miniForm || this.enterpriseList.length > 1;
	}
}
